import { makeAutoObservable, runInAction } from 'mobx';
import companyStore from '../../../store/company-store';
import professionalModel from '../../../model/professional/professional-model';
import {HTTP_STATUS_CODE} from "../../../utils/UHttp";
import toastStore from "../../../store/toast-store";

export interface Collaborator {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'OWNER' | 'ADMIN' | 'STAFF';
  avatar?: string;
}

export type ViewMode = 'list' | 'detail' | 'edit';

export class CompanyCollaboratorsViewModel {

  isAddCollaboratorModalOpen: boolean = false;
  isDeleteCollaboratorModalOpen: boolean = false;
  selectedCollaboratorId: string = '';
  collaboratorToDelete: any = null;
  companyRole: 'Owner' | 'Admin' | 'Staff' = 'Staff';
  error: string = '';
  viewMode: ViewMode = 'list';
  selectedCollaborator: any = null;
  isDeleting: boolean = false;

  private static instance: CompanyCollaboratorsViewModel;

  private constructor() {
    makeAutoObservable(this);
  }

  public static getInstance(): CompanyCollaboratorsViewModel {
    if (!CompanyCollaboratorsViewModel.instance) {
      CompanyCollaboratorsViewModel.instance = new CompanyCollaboratorsViewModel();
    }
    return CompanyCollaboratorsViewModel.instance;
  }

  setIsAddCollaboratorModalOpen(isOpen: boolean) {
    this.isAddCollaboratorModalOpen = isOpen;
  }

  setSelectedCollaboratorId(id: string) {
    this.selectedCollaboratorId = id;
  }

  setCompanyRole(role: 'Owner' | 'Admin' | 'Staff') {
    this.companyRole = role;
  }

  setError(error: string) {
    this.error = error;
  }

  setViewMode(mode: ViewMode) {
    this.viewMode = mode;
  }

  setSelectedCollaborator(collaborator: any) {
    this.selectedCollaborator = collaborator;
  }

  setIsDeleteCollaboratorModalOpen(isOpen: boolean) {
    this.isDeleteCollaboratorModalOpen = isOpen;
  }

  setCollaboratorToDelete(collaborator: any) {
    this.collaboratorToDelete = collaborator;
  }

  setIsDeleting(isDeleting: boolean) {
    this.isDeleting = isDeleting;
  }

  handleAddCollaborator = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone: string;
    companyId: string;
    roleId: string;
    permissionIds: string[];
  }) => {
    try {
      const response = await professionalModel.handleAddCollaborator(data);

      if(response.status === HTTP_STATUS_CODE.CREATED) {
        toastStore.success(response.message)
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toastStore.error(response.message)
      }

    } catch (error: any) {
      runInAction(() => {
        const errorMessage = error?.data?.message || 'Erreur lors de l\'ajout du collaborateur';
        this.setError(errorMessage);
      });
      console.error('Error handleAddCollaborator:', error);
      throw error;
    }
  };

  handleOpenDeleteModal = (collaborator: any) => {
    this.setCollaboratorToDelete(collaborator);
    this.setIsDeleteCollaboratorModalOpen(true);
  };

  handleCloseDeleteModal = () => {
    this.setIsDeleteCollaboratorModalOpen(false);
    this.setCollaboratorToDelete(null);
  };

  handleRemoveCollaborator = async () => {
    if (!this.collaboratorToDelete || !companyStore.getIdCompanySelectedDefault) {
      return;
    }

    try {
      this.setIsDeleting(true);

      // Appel API pour supprimer le collaborateur
      // Utiliser le collaboratorId (ID réel du collaborateur), pas l'ID de l'association
      const response = await professionalModel.handleDeleteCollaborator(
        companyStore.getIdCompanySelectedDefault,
        this.collaboratorToDelete.collaboratorId
      );


      if(response.status === HTTP_STATUS_CODE.OK) {
        toastStore.success(response.message);
        toastStore.success(response.message)
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toastStore.error(response.message);
      }

      // Recharger la page pour afficher la liste mise à jour
      //window.location.reload();

    } catch (error: any) {
      runInAction(() => {
        const errorMessage = error?.data?.message || 'Erreur lors de la suppression du collaborateur';
        this.setError(errorMessage);
        this.setIsDeleting(false);
      });
      console.error('Error handleRemoveCollaborator:', error);
    }
  };

  getRoleBadgeClass = (role: string): string => {
    switch (role) {
      case 'OWNER':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'STAFF':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  handleViewCollaborator = (collaborator: any) => {
    this.setSelectedCollaborator(collaborator);
    this.setViewMode('detail');
  };

  handleEditCollaborator = (collaborator: any) => {
    this.setSelectedCollaborator(collaborator);
    this.setViewMode('edit');
  };

  handleBackToList = () => {
    this.setViewMode('list');
    this.setSelectedCollaborator(null);
  };
}

export default CompanyCollaboratorsViewModel.getInstance();
